import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth, type AppRole } from "@/auth/AuthProvider";

const STORAGE_KEY = "os-impersonation";

export type ImpersonationTarget = {
  /** Stable id — user_id for Smart Canvas team, invitation_id for client contacts. */
  id: string;
  /** Auth user id, when known. Null for invited contacts not yet linked. */
  user_id: string | null;
  name: string;
  email: string | null;
  avatar_url: string | null;
  role: "operator" | "client";
  role_on_project?: string | null;
  projectId: string;
  projectName: string;
  startedAt: string;
};

type Ctx = {
  active: ImpersonationTarget | null;
  start: (t: Omit<ImpersonationTarget, "startedAt">) => void;
  stop: () => void;
};

const ImpersonationCtx = createContext<Ctx | undefined>(undefined);

function readStored(): ImpersonationTarget | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ImpersonationTarget) : null;
  } catch { return null; }
}

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<ImpersonationTarget | null>(() => readStored());

  const start = useCallback((t: Omit<ImpersonationTarget, "startedAt">) => {
    const full: ImpersonationTarget = { ...t, startedAt: new Date().toISOString() };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(full));
    setActive(full);
  }, []);

  const stop = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    setActive(null);
  }, []);

  // Cross-tab sync
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setActive(readStored());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const value = useMemo(() => ({ active, start, stop }), [active, start, stop]);
  return <ImpersonationCtx.Provider value={value}>{children}</ImpersonationCtx.Provider>;
}

export function useImpersonation(): Ctx {
  const v = useContext(ImpersonationCtx);
  if (!v) throw new Error("useImpersonation must be used inside ImpersonationProvider");
  return v;
}

/** Helper for write-paths: returns the acting user id (impersonated or current). */
export function getActingUserId(currentUserId: string | null | undefined): string | null {
  const stored = readStored();
  return stored?.user_id ?? currentUserId ?? null;
}

/**
 * Async resolver used by API modules (no React). Returns the impersonated
 * user_id if an impersonation session is active, otherwise the currently
 * authenticated user id. Used to attribute writes (created_by, author_id,
 * uploader_id, …) to the impersonated user when admins are "acting as".
 */
export async function resolveActingUid(): Promise<string | null> {
  const stored = readStored();
  if (stored?.user_id) return stored.user_id;
  const { data } = await supabase.auth.getUser();
  return data.user?.id ?? null;
}

/** Synchronous read — only available if previously cached or stored. */
export function getStoredImpersonation(): ImpersonationTarget | null {
  return readStored();
}

/**
 * React hook returning the *effective* identity for the current view. While
 * impersonation is active, components should treat this as the logged-in user
 * (id, role) so permission gates, "my tasks" highlighting and currentUserId
 * props reflect the impersonated person.
 */
export function useActingUser(): {
  userId: string | null;
  role: AppRole | null;
  isImpersonating: boolean;
  displayName: string | null;
} {
  const { user, primaryRole } = useAuth();
  const { active } = useImpersonation();
  if (active) {
    return {
      userId: active.user_id,
      role: active.role as AppRole,
      isImpersonating: true,
      displayName: active.name,
    };
  }
  return {
    userId: user?.id ?? null,
    role: primaryRole,
    isImpersonating: false,
    displayName: null,
  };
}